import * as Models from '@echotex/api-client-utility';
import { IResponse, ISuccessResponse } from '@core-web/networking';
import { useApi } from './useApi';
import { IApiData } from '../types/IApiData';
import { ApiClient, CLIENT_TYPES } from '../clients';
import { useDispatch, useSelector } from 'react-redux';

/**
 * The client init method
 */
const getClient = async (): Promise<Models.IEchoTexUtilityClientV1> => {
  return await ApiClient.getClient<Models.IEchoTexUtilityClientV1>(CLIENT_TYPES.UTIL_CLIENT);
};

/**
 * The method to trigger any redux events
 */
export const useUtilityRedux = async (type: Models.EventTypes): Promise<(payload: Models.IReducerPayload) => void> => {
  const dispatch = useDispatch();
  const client = await ApiClient.getClient<Models.IEchoTexUtilityClientV1>(CLIENT_TYPES.UTIL_CLIENT);
  return (payload: Models.IReducerPayload) => dispatch(client.triggerAction(type, payload));
};

/**
 * The method to trigger any redux events
 */
export const useUtilitySelector = (): Models.IUtilityReducer => {
  const auth = useSelector((state: any) => state.utility);
  return auth;
};

/**
 * The useGetFilePaths hook
 */
export const useGetFilePaths = (): IApiData<
  IResponse<Models.IGetFilePathsResponseBody>,
  null,
  Models.IGetFilePathsResponseBody
> => {
  const dispatch = useDispatch();
  return useApi<IResponse<Models.IGetFilePathsResponseBody>, null, Models.IGetFilePathsResponseBody>(async () => {
    const client = await getClient();
    return await client.getFilePaths(dispatch);
  });
};

/**
 * The useUploadImages hook
 */
export const useUploadImages = (): IApiData<
  IResponse<Models.IUploadImageResponseBody>,
  Models.IUploadImageRequestBody,
  Models.IUploadImageResponseBody
> => {
  return useApi<
    IResponse<Models.IUploadImageResponseBody>,
    Models.IUploadImageRequestBody,
    Models.IUploadImageResponseBody
  >(async (query) => {
    const client = await getClient();
    return await client.uploadImages(query);
  });
};

/**
 * The useUploadFiles hook
 */
export const useUploadFiles = (): IApiData<
  IResponse<Models.IUploadFilesResponseBody>,
  Models.IUploadFilesRequestBody,
  Models.IUploadFilesResponseBody
> => {
  return useApi<
    IResponse<Models.IUploadFilesResponseBody>,
    Models.IUploadFilesRequestBody,
    Models.IUploadFilesResponseBody
  >(async (query) => {
    const client = await getClient();
    return await client.uploadFiles(query);
  });
};

/**
 * The register supplier hook
 */
export const useGetDownloadUrl = (): IApiData<
  IResponse<Models.IGetDownloadUrlResponseBody>,
  Models.IGetDownloadUrlRequestBody,
  Models.IGetDownloadUrlResponseBody
> => {
  return useApi<
    IResponse<Models.IGetDownloadUrlResponseBody>,
    Models.IGetDownloadUrlRequestBody,
    Models.IGetDownloadUrlResponseBody
  >(async (query) => {
    const client = await getClient();
    return await client.getDownloadUrl(query);
  });
};

/**
 * The register supplier hook
 */
export const useGetDownloadUrlSas = (): IApiData<
  IResponse<Models.IGetDownloadUrlResponseBody>,
  Models.IGetDownloadUrlRequestBody,
  Models.IGetDownloadUrlResponseBody
> => {
  return useApi<
    IResponse<Models.IGetDownloadUrlResponseBody>,
    Models.IGetDownloadUrlRequestBody,
    Models.IGetDownloadUrlResponseBody
  >(async (query) => {
    const client = await getClient();
    return await client.getDownloadUrlSas(query);
  });
};

/**
 * The useDummyDeleteApiHook hook
 */
export const useDummyDeleteApiHook = (): IApiData<IResponse<ISuccessResponse>, null, ISuccessResponse> => {
  return useApi<IResponse<ISuccessResponse>, null, ISuccessResponse>(async () => {
    const client = await getClient();
    return await client.dummyDeleteMethod();
  });
};
